import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { motion, useReducedMotion } from 'motion/react';
import { ClipboardList, House, LayoutGrid, Menu as MenuIcon, ShoppingBag } from 'lucide-react';
import { cn, springs } from '@dezza/ui';
import { matchNavItem, useNav, useOrdersBadge } from './nav';
import { useShell } from './shell-store';

/** Altura da barra de abas (sem a área segura). Mantida em sincronia com --bottom-nav-offset do AppShell. */
export const BOTTOM_NAV_HEIGHT = '4rem';

/**
 * Teclado virtual aberto (campo de texto com foco). A barra de abas some enquanto a pessoa digita:
 * nada pula para cima do teclado nem cobre o campo.
 */
export function useTyping(): boolean {
  const [typing, setTyping] = useState(false);
  useEffect(() => {
    const editable = (el: Element | null) => {
      if (!el) return false;
      if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return true;
      if (el instanceof HTMLInputElement)
        return ![
          'checkbox',
          'radio',
          'button',
          'submit',
          'reset',
          'range',
          'color',
          'file',
          'image',
        ].includes(el.type);
      return (el as HTMLElement).isContentEditable === true;
    };
    const onIn = (e: FocusEvent) => setTyping(editable(e.target as Element));
    const onOut = () => window.setTimeout(() => setTyping(editable(document.activeElement)), 0);
    document.addEventListener('focusin', onIn);
    document.addEventListener('focusout', onOut);
    return () => {
      document.removeEventListener('focusin', onIn);
      document.removeEventListener('focusout', onOut);
    };
  }, []);
  return typing;
}

function Tab({
  to,
  label,
  icon,
  active,
  badge,
  onClick,
  expanded,
}: {
  to?: string;
  label: string;
  icon: ReactNode;
  active: boolean;
  badge?: number;
  onClick?: () => void;
  expanded?: boolean;
}) {
  const reduce = useReducedMotion();
  const inner = (
    <>
      <span className="relative flex h-8 w-14 items-center justify-center">
        {active ? (
          <motion.span
            layoutId="bottom-tab-pill"
            transition={reduce ? { duration: 0 } : springs.snappy}
            aria-hidden="true"
            className="absolute inset-0 rounded-full bg-primary-soft"
          />
        ) : null}
        <span className={cn('relative [&_svg]:size-[22px]', active ? 'text-primary-strong' : 'text-ink-3')}>
          {icon}
        </span>
        {badge ? (
          <span className="tabular absolute -top-1 right-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-on-primary ring-2 ring-surface">
            {badge > 99 ? '99+' : badge}
          </span>
        ) : null}
      </span>
      <span
        className={cn(
          'text-[11px] leading-none tracking-[-0.005em]',
          active ? 'font-semibold text-ink' : 'font-medium text-ink-3',
        )}
      >
        {label}
      </span>
    </>
  );
  const cls =
    'flex h-full min-w-0 flex-1 flex-col items-center justify-center gap-1 outline-none transition-[scale] duration-150 active:scale-[0.95] focus-visible:bg-ink/[0.05] motion-reduce:active:scale-100';
  const name = badge ? `${label}, ${badge} em aberto` : undefined;
  if (to) {
    return (
      <Link to={to} aria-current={active ? 'page' : undefined} aria-label={name} className={cls}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} aria-expanded={expanded} aria-haspopup="dialog" className={cls}>
      {inner}
    </button>
  );
}

/**
 * Barra de abas do celular: Início, Pedidos, Vender (ação central em destaque), Produtos e Mais
 * (abre a navegação completa). Só nas telas de primeiro nível e some com o teclado aberto.
 */
export function BottomNav() {
  const location = useLocation();
  const groups = useNav();
  const menuOpen = useShell((s) => s.menuOpen);
  const setMenuOpen = useShell((s) => s.setMenuOpen);
  const ordersBadge = useOrdersBadge();
  const visible = new Set(groups.flatMap((g) => g.items.map((i) => i.key)));
  const active =
    matchNavItem(location.pathname)?.key ?? (location.pathname === '/notificacoes' ? 'mais' : null);
  const tabKeys = ['inicio', 'pedidos', 'produtos'];
  const moreActive = menuOpen || (active !== null && !tabKeys.includes(active));

  return (
    <nav
      aria-label="Navegação rápida"
      className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/90 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl backdrop-saturate-150 animate-fade-in"
    >
      <div className="mx-auto flex h-16 max-w-lg items-stretch px-1">
        <Tab to="/inicio" label="Início" icon={<House />} active={!menuOpen && active === 'inicio'} />
        {visible.has('pedidos') ? (
          <Tab
            to="/pedidos"
            label="Pedidos"
            icon={<ClipboardList />}
            active={!menuOpen && active === 'pedidos'}
            badge={ordersBadge}
          />
        ) : null}
        {visible.has('vender') ? (
          <div className="flex min-w-0 flex-1 justify-center">
            <Link
              to="/vender"
              className="group -mt-5 flex flex-col items-center gap-1 outline-none"
              aria-label="Vender"
            >
              <span className="flex size-[52px] items-center justify-center rounded-full bg-primary text-on-primary shadow-[0_10px_24px_-8px_rgb(107_78_255/0.75),inset_0_1px_0_rgb(255_255_255/0.22)] ring-4 ring-bg transition-[scale,background-color] duration-150 group-hover:bg-primary-hover group-active:scale-[0.93] group-focus-visible:ring-ring motion-reduce:group-active:scale-100">
                <ShoppingBag className="size-6" strokeWidth={2.25} aria-hidden="true" />
              </span>
              <span className="text-[11px] font-semibold leading-none text-ink" aria-hidden="true">
                Vender
              </span>
            </Link>
          </div>
        ) : null}
        <Tab
          to="/produtos"
          label="Produtos"
          icon={<LayoutGrid />}
          active={!menuOpen && active === 'produtos'}
        />
        <Tab
          label="Mais"
          icon={<MenuIcon />}
          active={moreActive}
          onClick={() => setMenuOpen(true)}
          expanded={menuOpen}
        />
      </div>
    </nav>
  );
}
